/**
 * GoalMergePanel - Entity-specific merge panel for Goals
 * 
 * Provides a familiar tab-based UI (Overview | KPIs | Initiatives) for both
 * source items (view mode) and merged result (edit mode).
 * 
 * This replaces the generic dynamic merge UI when entityType === 'goal',
 * giving users a clear understanding of what data is being kept vs lost.
 * 
 * Props:
 * - sourceItems: array of goal objects fetched from DB
 * - selectedItems: { [idx]: boolean } - which source items are included
 * - expandedItems: { [idx]: boolean } - which source items are expanded
 * - onToggleSelect: (idx) => void
 * - onToggleExpand: (idx) => void
 * - formData: merged goal form data
 * - onFormChange: (field, value) => void
 * - teamMembers: array of team members for owner dropdown
 * - allProductInitiatives: array for initiative linking
 * - colors: theme colors (optional, will use useSafeMode if not provided)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { teamApi, initiativesApi } from '../../services/api';
import {
  ChevronDown,
  ChevronRight,
  Target,
  Layers,
  Rocket,
  Plus,
  Minus,
  AlertTriangle,
} from 'lucide-react';

// Import reusable form components
import GoalOverviewForm, { GOAL_STATUS_OPTIONS, GOAL_REQUIRED_FIELDS } from '../goal/GoalOverviewForm';
import GoalKPIsPanel, { generateKPIId, formatMetricValue } from '../goal/GoalKPIsPanel';
import GoalInitiativesPanel from '../goal/GoalInitiativesPanel';

// =============================================================================
// SOURCE GOAL CARD (Left Panel)
// =============================================================================

const SourceGoalCard = ({
  goal,
  index,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  teamMembers,
  colors,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const statusConfig = GOAL_STATUS_OPTIONS.find(s => s.value === goal.status) || GOAL_STATUS_OPTIONS[0];
  const ownerName = teamMembers.find(m => m.user_id === goal.owner_id)?.name || 'Unassigned';
  const kpiCount = goal.kpis?.length || 0;
  
  // Mock linked initiatives - in real implementation, these would be fetched
  const linkedInitiatives = goal.linked_initiatives || [];

  return (
    <div 
      className={cn(
        "rounded-lg border overflow-hidden transition-all",
        isSelected 
          ? "border-indigo-300 dark:border-indigo-600 bg-white dark:bg-gray-800" 
          : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60"
      )}
      data-testid={`source-goal-${index}`}
    >
      {/* Header - Always Visible */}
      <div 
        className={cn(
          "flex items-center gap-3 p-3 cursor-pointer",
          "hover:bg-gray-50 dark:hover:bg-gray-700/50"
        )}
        onClick={onToggleExpand}
      >
        {/* Selection Checkbox */}
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            className="border-gray-400"
          />
        </div>
        
        {/* Expand/Collapse Icon */}
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
        
        {/* Goal Icon */}
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          isSelected ? "bg-indigo-100 dark:bg-indigo-900/30" : "bg-gray-200 dark:bg-gray-700"
        )}>
          <Target className={cn("w-4 h-4", isSelected ? "text-indigo-600" : "text-gray-500")} />
        </div>
        
        {/* Goal Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              "font-medium truncate",
              isSelected ? "text-gray-900 dark:text-gray-100" : "text-gray-500 line-through"
            )}>
              {goal.title || 'Untitled Goal'}
            </span>
            {!isSelected && (
              <Badge variant="outline" className="text-xs text-red-500 border-red-300">
                Excluded
              </Badge>
            )}
          </div>
          {!isExpanded && (
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
              <span className={cn("px-1.5 py-0.5 rounded", statusConfig.color, "text-white text-[10px]")}>
                {statusConfig.label}
              </span>
              <span>•</span>
              <span>{ownerName}</span>
              {kpiCount > 0 && (
                <>
                  <span>•</span>
                  <span>{kpiCount} KPI{kpiCount !== 1 ? 's' : ''}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Expanded Content - Tab-based View */}
      {isExpanded && (
        <div className={cn("border-t", colors.borderPrimary)}>
          {/* Tabs */}
          <div className="px-3 pt-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-gray-100 dark:bg-gray-800">
                <TabsTrigger value="overview" className="text-xs">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="kpis" className="text-xs">
                  KPIs ({kpiCount})
                </TabsTrigger>
                <TabsTrigger value="initiatives" className="text-xs">
                  Initiatives ({linkedInitiatives.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          {/* Tab Content */}
          <div className="p-3">
            {activeTab === 'overview' && (
              <GoalOverviewForm
                formData={goal}
                isEditing={false}
                isDisabled={true}
                colors={colors}
                teamMembers={teamMembers}
                showStatusBanner={false}
                mode="view"
              />
            )}
            
            {activeTab === 'kpis' && (
              <GoalKPIsPanel
                kpis={goal.kpis || []}
                isEditing={false}
                status={goal.status}
                colors={colors}
                mode="view"
              />
            )}
            
            {activeTab === 'initiatives' && (
              <GoalInitiativesPanel
                linkedInitiatives={linkedInitiatives}
                isEditing={false}
                colors={colors}
                mode="view"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MERGE DIFF SUMMARY
// =============================================================================

const MergeDiffSummary = ({ sourceItems, selectedItems, mergedKpis, colors }) => {
  // Calculate what's being kept vs lost
  const selectedGoals = sourceItems.filter((_, idx) => selectedItems[idx]);
  const excludedGoals = sourceItems.filter((_, idx) => !selectedItems[idx]);
  
  // Count KPIs
  const totalSourceKpis = selectedGoals.reduce((sum, g) => sum + (g.kpis?.length || 0), 0);
  const mergedKpiCount = mergedKpis?.length || 0;
  const excludedKpis = excludedGoals.reduce((sum, g) => sum + (g.kpis?.length || 0), 0);
  
  // Count initiatives
  const totalSourceInitiatives = selectedGoals.reduce((sum, g) => sum + (g.linked_initiatives?.length || 0), 0);
  const excludedInitiatives = excludedGoals.reduce((sum, g) => sum + (g.linked_initiatives?.length || 0), 0);

  if (excludedGoals.length === 0 && excludedKpis === 0) {
    return null; // No diff to show
  }

  return (
    <div className={cn("p-3 rounded-lg border mb-4", "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700")}>
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-amber-800 dark:text-amber-200 text-sm">
          Merge Impact Summary
        </span>
      </div>
      <div className="space-y-1 text-xs">
        {excludedGoals.length > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedGoals.length} goal(s) excluded: {excludedGoals.map(g => `"${g.title}"`).join(', ')}
            </span>
          </div>
        )}
        {excludedKpis > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedKpis} KPI(s) from excluded goals will be lost
            </span>
          </div>
        )}
        {excludedInitiatives > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedInitiatives} initiative link(s) from excluded goals will be lost
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 pt-1 border-t border-amber-300 dark:border-amber-600 mt-2">
          <Plus className="w-3 h-3 text-green-500" />
          <span className="text-green-700 dark:text-green-300">
            Merged goal will have {mergedKpiCount} KPI(s) from {selectedGoals.length} selected goal(s)
          </span>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MERGED GOAL FORM (Right Panel)
// =============================================================================

const MergedGoalForm = ({
  formData,
  onFormChange,
  teamMembers,
  allProductInitiatives,
  linkedInitiatives,
  onLinkInitiative,
  onUnlinkInitiative,
  onCreateInitiative,
  colors,
  errors = {},
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const handleChange = (field, value) => {
    onFormChange(field, value);
  };
  
  // KPI handlers
  const handleAddKPI = () => {
    const newKpi = {
      kpi_id: generateKPIId(),
      metric_name: '',
      metric_format: 'number',
      direction: 'higher_is_better',
      measurement_interval: 'monthly',
      start_value: 0,
      target_value: 0,
      description: '',
    };
    onFormChange('kpis', [...(formData.kpis || []), newKpi]);
  };
  
  const handleUpdateKPI = (kpiId, field, value) => {
    const updated = (formData.kpis || []).map(kpi => 
      kpi.kpi_id === kpiId ? { ...kpi, [field]: value } : kpi
    );
    onFormChange('kpis', updated);
  };
  
  const handleDeleteKPI = (kpiId) => {
    const updated = (formData.kpis || []).filter(kpi => kpi.kpi_id !== kpiId);
    onFormChange('kpis', updated);
  };

  const kpiCount = formData.kpis?.length || 0;
  const initiativeCount = linkedInitiatives?.length || 0;
  
  // Check if there are errors in overview fields
  const hasOverviewErrors = Object.keys(errors).some(k => ['title', 'target_date', 'description'].includes(k));

  return (
    <div className="h-full flex flex-col">
      {/* Tabs */}
      <div className="flex-shrink-0 pb-2">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={cn("bg-gray-100 dark:bg-gray-800")}>
            <TabsTrigger 
              value="overview" 
              className={cn("text-sm", hasOverviewErrors && "text-red-500")}
            >
              Overview {hasOverviewErrors && '⚠️'}
            </TabsTrigger>
            <TabsTrigger value="kpis" className="text-sm">
              KPIs ({kpiCount})
            </TabsTrigger>
            <TabsTrigger value="initiatives" className="text-sm">
              Initiatives ({initiativeCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <GoalOverviewForm
            formData={formData}
            onChange={handleChange}
            isEditing={true}
            isDisabled={false}
            colors={colors}
            teamMembers={teamMembers}
            showStatusBanner={false}
            errors={errors}
            mode="create"
          />
        )}
        
        {activeTab === 'kpis' && (
          <GoalKPIsPanel
            kpis={formData.kpis || []}
            onAddKPI={handleAddKPI}
            onUpdateKPI={handleUpdateKPI}
            onDeleteKPI={handleDeleteKPI}
            isEditing={true}
            status="draft"
            colors={colors}
            mode="create"
          />
        )}
        
        {activeTab === 'initiatives' && (
          <GoalInitiativesPanel
            linkedInitiatives={linkedInitiatives}
            allProductInitiatives={allProductInitiatives}
            onLink={onLinkInitiative}
            onUnlink={onUnlinkInitiative}
            onCreateNew={onCreateInitiative}
            isEditing={true}
            colors={colors}
            canEdit={true}
            mode="edit"
          />
        )}
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const GoalMergePanel = ({
  sourceItems,
  selectedItems,
  expandedItems,
  onToggleSelect,
  onToggleExpand,
  formData,
  onFormChange,
  productId,
  errors = {},
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Team members for owner dropdown
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  
  // All product initiatives for linking
  const [allProductInitiatives, setAllProductInitiatives] = useState([]);
  const [loadingInitiatives, setLoadingInitiatives] = useState(true);
  
  // Linked initiatives for merged goal (local state)
  const [linkedInitiatives, setLinkedInitiatives] = useState([]);
  
  // Load team members
  useEffect(() => {
    const loadTeam = async () => {
      try {
        const res = await teamApi.list();
        const activeMembers = (res.data?.members || []).filter(m => m.status === 'active');
        setTeamMembers(activeMembers);
      } catch (err) {
        console.error('Failed to load team:', err);
      } finally {
        setLoadingTeam(false);
      }
    };
    loadTeam();
  }, []);
  
  // Load all product initiatives
  useEffect(() => {
    const loadInitiatives = async () => {
      if (!productId) return;
      try {
        const res = await initiativesApi.list(productId);
        setAllProductInitiatives(res.data || []);
      } catch (err) {
        console.error('Failed to load initiatives:', err);
      } finally {
        setLoadingInitiatives(false);
      }
    };
    loadInitiatives();
  }, [productId]);
  
  // Initialize linked initiatives from selected source goals
  useEffect(() => {
    const selectedGoals = sourceItems.filter((_, idx) => selectedItems[idx]);
    const allLinkedIds = new Set();
    const allLinked = [];
    
    selectedGoals.forEach(goal => {
      (goal.linked_initiatives || []).forEach(init => {
        if (!allLinkedIds.has(init.initiative_id)) {
          allLinkedIds.add(init.initiative_id);
          allLinked.push(init);
        }
      });
    });
    
    setLinkedInitiatives(allLinked);
  }, [sourceItems, selectedItems]);
  
  // Initiative handlers
  const handleLinkInitiative = (initiative) => {
    if (!linkedInitiatives.some(i => i.initiative_id === initiative.initiative_id)) {
      setLinkedInitiatives([...linkedInitiatives, initiative]);
    }
  };
  
  const handleUnlinkInitiative = (initiativeId) => {
    setLinkedInitiatives(linkedInitiatives.filter(i => i.initiative_id !== initiativeId));
  };
  
  const handleCreateInitiative = async (title) => {
    // Note: In merge context, we just add to local state. 
    // Actual creation happens when merge is executed.
    const tempInit = {
      initiative_id: `temp_${Date.now()}`,
      title,
      status: 'draft',
      _isNew: true,
    };
    setLinkedInitiatives([...linkedInitiatives, tempInit]);
    return tempInit;
  };
  
  // Selected count
  const selectedCount = Object.values(selectedItems).filter(Boolean).length;

  return (
    <div className="grid grid-cols-2 h-full divide-x divide-gray-200 dark:divide-gray-700">
      {/* Left Panel: Source Goals */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Source Goals
            </h3>
            <Badge variant="outline">
              {selectedCount} of {sourceItems.length} selected
            </Badge>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Select goals to include in the merge. Expand to review data.
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {sourceItems.map((goal, idx) => (
            <SourceGoalCard
              key={goal.goal_id || idx}
              goal={goal}
              index={idx}
              isSelected={selectedItems[idx]}
              isExpanded={expandedItems[idx]}
              onToggleSelect={() => onToggleSelect(idx)}
              onToggleExpand={() => onToggleExpand(idx)}
              teamMembers={teamMembers}
              colors={colors}
            />
          ))}
        </div>
      </div>
      
      {/* Right Panel: Merged Goal Form */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">
            Merged Result
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Edit the combined goal before applying
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {/* Diff Summary */}
          <MergeDiffSummary
            sourceItems={sourceItems}
            selectedItems={selectedItems}
            mergedKpis={formData.kpis}
            colors={colors}
          />
          
          {/* Merged Form */}
          <MergedGoalForm
            formData={formData}
            onFormChange={onFormChange}
            teamMembers={teamMembers}
            allProductInitiatives={allProductInitiatives}
            linkedInitiatives={linkedInitiatives}
            onLinkInitiative={handleLinkInitiative}
            onUnlinkInitiative={handleUnlinkInitiative}
            onCreateInitiative={handleCreateInitiative}
            colors={colors}
            errors={errors}
          />
        </div>
      </div>
    </div>
  );
};

export default GoalMergePanel;
